
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  CalendarDays,
  CarFront,
  CheckCircle2,
  Clock3,
  MapPin,
  Phone,
  Route,
} from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";

import BookingModal from "../BookingModal";
import "./HomeShowcase.css";

import statueOfUnityImage from "../../assets/destination-statue-of-unity.webp";
import pavagadhImage from "../../assets/destination-pavagadh.jpg";
import poichaImage from "../../assets/destination-poicha.jpg";
import dakorImage from "../../assets/destination-dakor.jpg";
import kaviKamboiImage from "../../assets/destination-kavi-kamboi.jpg";

const PHONE = "+917574098964";
const EMAIL = "info@bholecabservices.com";
const WHATSAPP_PHONE = PHONE.replace(/\D/g, "");

const WHATSAPP_MESSAGE = encodeURIComponent(
  "Hello Bhole Cab Service, I want to enquire about a cab booking from Vadodara."
);

const GOOGLE_MAPS_URL =
  "https://www.google.com/maps/search/?api=1&query=Vadodara%2C%20Gujarat";

const destinations = [
  {
    slug: "statue-of-unity",
    title: "Vadodara to Statue of Unity Taxi Cabs Booking",
    place: "Statue of Unity",
    label: "ICONIC GUJARAT GETAWAY",
    image: statueOfUnityImage,
    description:
      "Plan a comfortable family outing to Kevadia with a private cab from Vadodara.",
  },
  {
    slug: "pavagadh",
    title: "Vadodara to Pavagadh Taxi Cabs Booking",
    place: "Pavagadh",
    label: "SPIRITUAL HILL JOURNEY",
    image: pavagadhImage,
    description:
      "Arrange pickup and return travel for your visit to Kalika Mata Temple.",
  },
  {
    slug: "poicha",
    title: "Vadodara to Poicha Cabs - Best Taxi Service 24/7",
    place: "Poicha Nilkanth Dham",
    label: "TEMPLE & FAMILY TRIP",
    image: poichaImage,
    description:
      "Book a one-way or return cab for a relaxed visit to Nilkanth Dham.",
  },
  {
    slug: "dakor",
    title: "Vadodara to Dakor Taxi Cabs Booking",
    place: "Dakor",
    label: "PILGRIMAGE CAB SERVICE",
    image: dakorImage,
    description:
      "Plan your darshan trip with convenient pickup from Vadodara.",
  },
  {
    slug: "kavi-kamboi-mahadev-mandir",
    title: "Vadodara to Kavi Kamboi Taxi Cabs Booking from Rs 2000",
    place: "Kavi Kamboi Mahadev Mandir",
    label: "SACRED COASTAL ROUTE",
    image: kaviKamboiImage,
    description:
      "Ask us to plan a private cab for your visit to the coastal Mahadev temple.",
  },
];

export default function HomeShowcase({ mode = "all" }) {
  const [bookingOpen, setBookingOpen] = useState(false);

  const showBooking = mode === "all" || mode === "booking";
  const showDestinations = mode === "all" || mode === "destinations";

  const openBooking = () => {
    setBookingOpen(true);
  };

  const closeBooking = () => {
    setBookingOpen(false);
  };

  // Lock page scrolling while the booking modal is open.
  useEffect(() => {
    if (!bookingOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [bookingOpen]);

  return (
    <>
      {/* ==================== ONLINE BOOKING ==================== */}
      {showBooking && (
        <section
          className="bh-home-booking"
          aria-labelledby="bh-booking-title"
        >
          <div className="bh-booking-car-shape" aria-hidden="true">
            <CarFront size={76} strokeWidth={1.15} />
          </div>

          <div className="bh-booking-inner">
            <div className="bh-booking-heading">
              <span className="bh-section-kicker">
                <CalendarDays size={15} />
                ONLINE TAXI BOOKING
              </span>

              <h2 id="bh-booking-title">
                Book your next ride <em>with ease.</em>
              </h2>

              <p>
                Tell us where you want to go. Our team can help with the
                route, vehicle and fare.
              </p>

              <div className="bh-booking-points">
                <span>
                  <CheckCircle2 size={16} />
                  Pickup planning
                </span>

                <span>
                  <CheckCircle2 size={16} />
                  One-way &amp; return
                </span>

                <span>
                  <CheckCircle2 size={16} />
                  Direct confirmation
                </span>
              </div>
            </div>

            <div className="bh-booking-action-card">
              <div className="bh-booking-card-top">
                <span className="bh-booking-icon">
                  <Route size={22} />
                </span>

                <div>
                  <strong>Plan your trip</strong>
                  <small>Booking enquiry in a few steps</small>
                </div>
              </div>

              <button
                type="button"
                className="bh-showcase-button"
                onClick={openBooking}
              >
                Start booking
                <ArrowUpRight size={18} />
              </button>

              <a className="bh-booking-phone" href={`tel:${PHONE}`}>
                <Phone size={17} />
                Call +91 75740 98964
              </a>

              <small className="bh-booking-note">
                Fare and availability are confirmed directly with our team.
              </small>
            </div>
          </div>
        </section>
      )}

      {/* ==================== DESTINATION CARDS ==================== */}
      {showDestinations && (
        <section
          className="bh-destinations-section"
          id="tour-packages"
        >
          <div className="bh-showcase-container">
            <div className="bh-showcase-section-heading">
              <span className="bh-section-kicker">
                <MapPin size={15} />
                TOUR PACKAGES FROM VADODARA
              </span>

              <h2>
                Where will your <em>next journey</em> take you?
              </h2>

              <p>
                Explore popular temple trips and Gujarat destinations with
                Bhole Cab Service.
              </p>
            </div>

            <div className="bh-destination-grid">
              {destinations.map((place, index) => (
                <article
                  className="bh-destination-card"
                  key={place.slug}
                >
                  <Link
                    to={`/destinations/${place.slug}`}
                    className="bh-destination-image"
                    aria-label={`View ${place.place} cab details`}
                  >
                    <img
                      src={place.image}
                      alt={`${place.place} travel destination`}
                      loading="lazy"
                    />

                    <span className="bh-destination-index">
                      {String(index + 1).padStart(2, "0")}
                    </span>

                    <span className="bh-destination-arrow">
                      <ArrowUpRight size={20} />
                    </span>
                  </Link>

                  <div className="bh-destination-content">
                    <span className="bh-destination-label">
                      {place.label}
                    </span>

                    <h3>
                      <Link to={`/destinations/${place.slug}`}>
                        {place.title}
                      </Link>
                    </h3>

                    <p>{place.description}</p>

                    <Link
                      className="bh-destination-readmore"
                      to={`/destinations/${place.slug}`}
                    >
                      Explore route
                      <ArrowUpRight size={16} />
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ==================== CALL TO ACTION ==================== */}
      {showDestinations && (
        <section className="bh-route-strip">
          <div className="bh-route-strip-inner">
            <div className="bh-route-strip-copy">
              <span>YOUR NEXT TRIP STARTS HERE</span>

              <h2>Need a cab for your destination?</h2>

              <p>
                Get in touch to discuss pickup time, passengers, return
                plans and fare.
              </p>
            </div>

            <div className="bh-route-strip-actions">
              <a
                href={`tel:${PHONE}`}
                className="bh-route-call"
              >
                <Phone size={19} />

                <span>
                  <small>CALL OUR TEAM</small>
                  <strong>+91 75740 98964</strong>
                </span>
              </a>

              <a
                className="bh-showcase-button bh-showcase-button--light bh-whatsapp-button"
                href={`https://wa.me/${WHATSAPP_PHONE}?text=${WHATSAPP_MESSAGE}`}
                target="_blank"
                rel="noreferrer"
              >
                <FaWhatsapp size={18} />
                WhatsApp us
              </a>

              <button
                type="button"
                className="bh-showcase-button bh-showcase-button--dark"
                onClick={openBooking}
              >
                Book a taxi
                <ArrowUpRight size={18} />
              </button>
            </div>
          </div>
        </section>
      )}

      {/* ==================== CONTACT & REAL MAP ==================== */}
      {showDestinations && (
        <section className="bh-contact-map-section">
          <div className="bh-showcase-container bh-contact-map-grid">
            <div className="bh-contact-map-copy">
              <span className="bh-section-kicker">
                <MapPin size={15} />
                CONTACT &amp; PICKUP
              </span>

              <h2>
                Let’s get you <em>on the road.</em>
              </h2>

              <p>
                Contact Bhole Cab Service to arrange a pickup in Vadodara
                and plan your local or outstation journey.
              </p>

              <div className="bh-contact-map-details">
                <div>
                  <span className="bh-contact-detail-icon">
                    <Phone size={18} />
                  </span>

                  <span>
                    <small>CALL FOR BOOKINGS</small>
                    <a href={`tel:${PHONE}`}>+91 75740 98964</a>
                  </span>
                </div>

                <div>
                  <span className="bh-contact-detail-icon">
                    <MapPin size={18} />
                  </span>

                  <span>
                    <small>BASE LOCATION</small>
                    <strong>Vadodara, Gujarat</strong>
                  </span>
                </div>

                <div>
                  <span className="bh-contact-detail-icon">
                    <Clock3 size={18} />
                  </span>

                  <span>
                    <small>TRIP PLANNING</small>
                    <strong>Pickup and return by arrangement</strong>
                  </span>
                </div>
              </div>

              <div className="bh-contact-map-actions">
                <Link
                  to="/contact"
                  className="bh-showcase-button"
                >
                  Contact us
                  <ArrowUpRight size={18} />
                </Link>

                <a
                  href={`mailto:${EMAIL}`}
                  className="bh-email-link"
                >
                  Email our team
                </a>
              </div>
            </div>

            <div className="bh-map-card">
              <div className="bh-map-embed">
                <iframe
                  title="Vadodara, Gujarat Map"
                  src="https://maps.google.com/maps?q=Vadodara%2C%20Gujarat%2C%20India&t=&z=12&ie=UTF8&iwloc=&output=embed"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                />
              </div>

              <a
                className="bh-map-card-bottom"
                href={GOOGLE_MAPS_URL}
                target="_blank"
                rel="noreferrer"
                aria-label="Open Vadodara in Google Maps"
              >
                <div>
                  <strong>Vadodara, Gujarat</strong>
                  <span>
                    Explore the city and find your pickup area
                  </span>
                </div>

                <ArrowUpRight size={22} />
              </a>
            </div>
          </div>
        </section>
      )}

      {/* ==================== BOOKING MODAL ==================== */}
      <BookingModal
        isOpen={bookingOpen}
        onClose={closeBooking}
      />
    </>
  );
}
